import clsx from "clsx";

import { Checkbox } from "react-migration/components/DeprecatedCheckbox";

import type { RecursiveTree } from "./types";
import { CheckboxItem } from "./TreeBuilders/checkboxTreeRecursive";
import { CollapsibleItem } from "./TreeBuilders/collapsibleTreeRecursive";
import { LabelItem } from "./TreeBuilders/labelTreeRecursive";

interface CheckboxTreeListProps<T> {
  disabled?: boolean;
  columns?: boolean;
  trees: RecursiveTree<T>[];
}

export function CheckboxTreeList<T extends CheckboxItem & CollapsibleItem & LabelItem>({
  disabled,
  columns,
  trees,
}: Readonly<CheckboxTreeListProps<T>>) {
  return (
    <ul
      className={clsx("list-none", {
        "flex flex-col gap-1": !columns,
        "columns-2 gap-6": columns,
      })}
    >
      {trees.map((tree) => (
        <li key={tree.id} className="list-none">
          <CheckboxTreeNode disabled={disabled} tree={tree} />
        </li>
      ))}
    </ul>
  );
}

interface CheckboxTreeNodeProps<T> {
  disabled?: boolean;
  tree: RecursiveTree<T>;
}

export function CheckboxTreeNode<T extends CheckboxItem & CollapsibleItem & LabelItem>({
  disabled,
  tree,
}: Readonly<CheckboxTreeNodeProps<T>>) {
  let label = tree.label;

  if (tree.isCollapsible) {
    label = (
      <button
        disabled={disabled}
        className="flex items-center gap-1 text-left"
        onClick={() => tree.setCollapsed(!tree.collapsed)}
      >
        <i
          className={clsx(
            "text-base",
            "w-5",
            "h-5",
            tree.collapsed ? "icon-lt-arrow-right-s-line" : "icon-lt-arrow-down-s-line"
          )}
        />

        {label}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label
        className="flex! justify-between items-center select-none text-sm font-medium"
        data-testid="checkbox-tree-node-label"
      >
        {label}
        <Checkbox disabled={disabled} value={tree.checkboxState} onChange={tree.handleCheckboxChange} />
      </label>
      {!!tree.childNodes.length && (
        <div
          className={clsx("ml-6", {
            hidden: tree.isCollapsible && tree.collapsed,
          })}
        >
          <CheckboxTreeList disabled={disabled} trees={tree.childNodes} />
        </div>
      )}
    </div>
  );
}
